import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { storage } from './firebase'

/* ============================================================================
   File uploads.

   Same Storage bucket and same paths as the native apps — `PosterUploader`
   writes `posters/{uuid}.{ext}` with a real content type, and so does this. A
   poster uploaded from the browser has to be indistinguishable from one
   uploaded on the Mac, because the same `tour.posterURL` renders in all three.
   ============================================================================ */

/** 10MB. Tour posters are print-resolution artwork, not photos off a phone. */
const MAX_BYTES = 10 * 1024 * 1024

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif']

export class UploadError extends Error {}

export interface UploadHandle {
  /** Resolves to the public download URL. */
  done: Promise<string>
  cancel: () => void
}

/**
 * Uploads an image and reports progress.
 *
 * Resumable rather than a single `uploadBytes` so a 9MB poster on venue wifi
 * shows a bar instead of an apparently frozen dialog — and so it can be
 * cancelled, which matters when someone drops the wrong file.
 */
export function uploadImage(
  file: File,
  folder: string,
  onProgress?: (fraction: number) => void,
): UploadHandle {
  validate(file)

  const extension = extensionFor(file)
  const path = `${folder}/${crypto.randomUUID()}.${extension}`
  const task = uploadBytesResumable(ref(storage, path), file, {
    // Naming a PNG ".jpg" and declaring the wrong type makes the CDN serve a
    // mislabelled image, so the type comes from the file itself.
    contentType: file.type || 'image/jpeg',
  })

  const done = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        if (snapshot.totalBytes > 0) {
          onProgress?.(snapshot.bytesTransferred / snapshot.totalBytes)
        }
      },
      (error) => {
        if (error.code === 'storage/canceled') {
          reject(new UploadError('Upload cancelled.'))
          return
        }
        if (error.code === 'storage/unauthorized') {
          reject(new UploadError("You don't have permission to upload here."))
          return
        }
        reject(new UploadError(`Upload failed: ${error.message}`))
      },
      () => {
        getDownloadURL(task.snapshot.ref).then(resolve, reject)
      },
    )
  })

  return { done, cancel: () => task.cancel() }
}

export function uploadPoster(
  file: File,
  onProgress?: (fraction: number) => void,
): UploadHandle {
  return uploadImage(file, 'posters', onProgress)
}

function validate(file: File) {
  if (file.size === 0) {
    throw new UploadError('That image file is empty.')
  }
  if (file.size > MAX_BYTES) {
    throw new UploadError(
      `That image is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_BYTES)}.`,
    )
  }
  // Some browsers report an empty type for HEIC; fall back to the extension
  // rather than rejecting a file the user can plainly see is a photo.
  const type = file.type || guessTypeFromName(file.name)
  if (!ALLOWED.includes(type)) {
    throw new UploadError('That file is not an image Encore can use. Try a JPG, PNG or WebP.')
  }
}

function extensionFor(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase()
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName
  return file.type.split('/').pop() ?? 'jpg'
}

function guessTypeFromName(name: string): string {
  const extension = name.split('.').pop()?.toLowerCase() ?? ''
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'png':
      return 'image/png'
    case 'webp':
      return 'image/webp'
    case 'heic':
      return 'image/heic'
    case 'heif':
      return 'image/heif'
    case 'gif':
      return 'image/gif'
    default:
      return ''
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
